import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Bell, MessageCircle, CheckCircle2, Copy, ExternalLink } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NotificationsPanel — slide-in drawer showing this staff-member's
 * notification channels + subscriptions.
 *
 * Two blocks:
 *   1. Telegram — status + Connect / Disconnect / Test.
 *      Deep-link is generated on-demand via /admin/telegram/connect-init and
 *      shown inline (copy button + t.me open button).
 *   2. Event subscriptions (placeholder for future — currently just informational).
 */
export default function NotificationsPanel({ onClose }) {
  const [tg, setTg] = useState(null);       // Telegram status
  const [link, setLink] = useState(null);   // {url, code, expires_at}
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [feed, setFeed] = useState(null);   // events feed

  const loadFeed = async () => {
    try {
      const r = await axios.get(`${API}/admin/notifications/feed`, { withCredentials: true });
      setFeed(r.data.items || []);
      if ((r.data.unread || 0) > 0) {
        await axios.post(`${API}/admin/notifications/feed/read`, {}, { withCredentials: true });
      }
    } catch { setFeed([]); }
  };

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/telegram/status`, { withCredentials: true });
      setTg(r.data);
    } catch { setTg(null); }
  };

  useEffect(() => { loadStatus(); loadFeed(); }, []);

  const connect = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await axios.post(`${API}/admin/telegram/connect-init`, {}, { withCredentials: true });
      setLink(r.data);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Не удалось получить ссылку");
    }
    setBusy(false);
  };
  const disconnect = async () => {
    if (!window.confirm("Отвязать Telegram от вашего аккаунта?")) return;
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/admin/telegram/disconnect`, {}, { withCredentials: true });
      setLink(null);
      await loadStatus();
    } catch (e) { setMsg(e.response?.data?.detail || "Ошибка"); }
    setBusy(false);
  };
  const sendTest = async () => {
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/admin/telegram/test`, {}, { withCredentials: true });
      setMsg("✓ Тест отправлен — проверьте Telegram");
    } catch (e) { setMsg(e.response?.data?.detail || "Ошибка"); }
    setBusy(false);
  };
  const copyCode = (code) => {
    try { navigator.clipboard?.writeText(code); setMsg("Код скопирован"); }
    catch { setMsg("Не удалось скопировать"); }
  };
  const copyLink = (url) => {
    try { navigator.clipboard?.writeText(url); setMsg("Ссылка скопирована — пришлите её сотруднику в WhatsApp/SMS"); }
    catch { setMsg("Не удалось скопировать ссылку"); }
  };
  // Belt-and-suspenders: some browsers (or PWA-mode) silently swallow
  // <a target="_blank" href="t.me/…">. This handler is a fallback that ALWAYS
  // triggers a new-tab navigation on click.
  const openDeepLink = (url) => (e) => {
    if (!url) { e?.preventDefault?.(); setMsg("Ссылка не сгенерирована"); return; }
    try { window.open(url, "_blank", "noopener"); } catch { /* popup blocked */ }
  };

  return (
    <div data-testid="notifications-panel-overlay"
         onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 250,
                  display: "flex", justifyContent: "flex-end" }}>
      <div data-testid="notifications-panel"
           onClick={(e) => e.stopPropagation()}
           style={{ width: "100%", maxWidth: "420px", background: "white", height: "100vh",
                    overflowY: "auto", boxShadow: "-8px 0 24px rgba(0,0,0,0.15)",
                    display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid #e5e7eb",
                       display: "flex", alignItems: "center", justifyContent: "space-between",
                       position: "sticky", top: 0, background: "white", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Bell size={18} style={{ color: "#C5A059" }} />
            <div>
              <div style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", fontWeight: 500, lineHeight: 1 }}>Уведомления</div>
              <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#6b7280", marginTop: "3px" }}>
                Настройки каналов и подписок
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="notifications-close-btn"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: "18px 20px", flex: 1 }}>
          {/* Events feed */}
          <SectionTitle>Лента событий</SectionTitle>
          <div data-testid="notifications-feed" style={{ marginBottom: "22px" }}>
            {feed === null && <Skeleton />}
            {feed && feed.length === 0 && (
              <div style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "#9ca3af", padding: "10px 0" }}>
                Событий пока нет — здесь появятся регистрации новых клиентов.
              </div>
            )}
            {feed && feed.slice(0, 20).map(n => (
              <div key={n.id} data-testid={`feed-item-${n.id}`}
                style={{ display: "flex", gap: "10px", padding: "10px 12px", marginBottom: "6px",
                         background: n.read ? "#fafafa" : "#FFFBEB",
                         border: `1px solid ${n.read ? "#f1f5f9" : "#FDE68A"}`, borderRadius: "8px" }}>
                <div style={{ width: "8px", height: "8px", borderRadius: "50%", marginTop: "5px", flexShrink: 0,
                              background: n.read ? "#d1d5db" : "#C5A059" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "Manrope", fontSize: "12.5px", fontWeight: 700, color: "#0A0A0A" }}>{n.title}</div>
                  {n.body && <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "#4b5563", marginTop: "2px" }}>{n.body}</div>}
                  <div style={{ fontFamily: "Manrope", fontSize: "10.5px", color: "#9ca3af", marginTop: "3px" }}>
                    {n.created_at ? new Date(n.created_at).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Telegram block */}
          <SectionTitle>Telegram-бот</SectionTitle>
          {tg === null && <Skeleton />}

          {tg && !tg.configured && (
            <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: "8px",
                          padding: "12px 14px", fontFamily: "Manrope", fontSize: "12px", color: "#78350f" }}>
              💬 Telegram-бот не настроен на сервере. Обратитесь к разработчику
              для добавления `TELEGRAM_BOT_TOKEN` в переменные окружения.
            </div>
          )}

          {tg && tg.configured && (
            <div style={{ background: "linear-gradient(135deg,#EEF6FF,#FFF)",
                          border: "1px solid #B4DAFE", borderRadius: "10px", padding: "16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "#229ED9",
                              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <MessageCircle size={18} style={{ color: "white" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "Manrope", fontSize: "13.5px", fontWeight: 700, color: "#1e40af" }}>
                    @{tg.bot_username}
                  </div>
                  <div style={{ fontFamily: "Manrope", fontSize: "11.5px", color: "#4b5563", marginTop: "2px", lineHeight: 1.5 }}>
                    Получайте новые лиды, задачи и просрочки прямо в Telegram.
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {tg.connected ? (
                  <>
                    <span data-testid="tg-status-connected"
                          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 10px",
                                   background: "#dcfce7", color: "#15803d", borderRadius: "999px",
                                   fontFamily: "Manrope", fontSize: "11px", fontWeight: 700 }}>
                      <CheckCircle2 size={12} /> Подключено{tg.telegram_username ? ` · @${tg.telegram_username}` : ""}
                    </span>
                    <button data-testid="tg-test-btn" onClick={sendTest} disabled={busy}
                      style={smallBtn}>Тест</button>
                    <button data-testid="tg-disconnect-btn" onClick={disconnect} disabled={busy}
                      style={{ ...smallBtn, color: "#dc2626", borderColor: "#fecaca" }}>
                      Отвязать
                    </button>
                  </>
                ) : (
                  <button data-testid="tg-connect-btn" onClick={connect} disabled={busy}
                    style={{ background: "linear-gradient(135deg,#229ED9,#1E88E5)", color: "white",
                             border: "none", borderRadius: "6px", padding: "10px 16px", cursor: "pointer",
                             fontFamily: "Manrope", fontSize: "12px", fontWeight: 700,
                             letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    {busy ? "..." : "Подключить"}
                  </button>
                )}
              </div>

              {link && !tg.connected && (
                <div data-testid="tg-link-instructions"
                     style={{ marginTop: "12px", padding: "12px 14px", background: "white",
                               border: "1px solid #B4DAFE", borderRadius: "8px", fontFamily: "Manrope", fontSize: "12px" }}>
                  <div style={{ fontWeight: 700, color: "#1e40af", marginBottom: "6px" }}>Как подключить (3 способа):</div>
                  <ol style={{ margin: "0 0 10px 20px", padding: 0, lineHeight: 1.6, color: "#374151" }}>
                    <li><b>Сотрудник у вас рядом с телефоном?</b> Пусть отсканирует QR-код камерой → откроется Telegram → нажмёт <b>Start</b>.</li>
                    <li><b>Сотрудник удалённо?</b> Нажмите «Скопировать ссылку» и отправьте её в WhatsApp/SMS. Он откроет её на своём телефоне.</li>
                    <li><b>Вы сами подключаетесь?</b> Нажмите «Открыть в Telegram» — откроется в Telegram Desktop или Web.</li>
                  </ol>

                  {/* QR — прямое решение для физической передачи телефона сотрудника */}
                  {link.deep_link && (
                    <div style={{ display: "flex", justifyContent: "center", padding: "10px", background: "white",
                                   border: "1px solid #dbeafe", borderRadius: "8px", marginBottom: "10px" }}>
                      <div style={{ textAlign: "center" }}>
                        <QRCodeSVG data-testid="tg-qr-code" value={link.deep_link} size={168} level="M" includeMargin={false} />
                        <div style={{ fontSize: "10.5px", color: "#6b7280", marginTop: "6px" }}>Наведите камеру телефона</div>
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    <a href={link.deep_link || "#"} target="_blank" rel="noreferrer"
                       data-testid="tg-open-deep-link"
                       onClick={openDeepLink(link.deep_link)}
                       style={{ background: link.deep_link ? "#229ED9" : "#94a3b8", color: "white", border: "none", borderRadius: "4px",
                                padding: "8px 12px", cursor: link.deep_link ? "pointer" : "not-allowed", textDecoration: "none",
                                fontFamily: "Manrope", fontSize: "11px", fontWeight: 700,
                                letterSpacing: "0.05em", textTransform: "uppercase",
                                display: "flex", alignItems: "center", gap: "5px" }}>
                      <ExternalLink size={12} /> Открыть в Telegram
                    </a>
                    <button data-testid="tg-copy-link-btn" onClick={() => copyLink(link.deep_link)} style={smallBtn}>
                      <Copy size={11} /> Скопировать ссылку
                    </button>
                    <button onClick={() => copyCode(link.code)} style={smallBtn} title="Код для ручного /start в боте">
                      <Copy size={11} /> Код: {link.code}
                    </button>
                    <button onClick={loadStatus} style={smallBtn}>Обновить статус</button>
                  </div>
                  <div style={{ fontSize: "10.5px", color: "#8a8a89", marginTop: "8px" }}>
                    Ссылка действительна до {link.expires_at ? new Date(link.expires_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }) : "15 минут"}.
                    Бот: <b>@{link.bot_username || "crm_manager_angiolini_bot"}</b>
                  </div>
                </div>
              )}

              {msg && <div style={{ marginTop: "10px", fontFamily: "Manrope", fontSize: "11.5px", color: msg.startsWith("✓") ? "#15803d" : "#8a6d2a" }}>{msg}</div>}
            </div>
          )}

          {/* Event subscriptions — informational only for now */}
          <SectionTitle style={{ marginTop: "26px" }}>Что вам будет приходить</SectionTitle>
          <div style={{ background: "#fafaf9", border: "1px solid #eee", borderRadius: "8px", padding: "14px",
                        fontFamily: "Manrope", fontSize: "12px", color: "#374151", lineHeight: 1.7 }}>
            <SubItem label="Новые лиды" active hint="Клиент оставил заявку через WhatsApp/сайт" />
            <SubItem label="Просроченные задачи" active hint="Задача не выполнена в срок" />
            <SubItem label="Активность по клиенту" active hint="Ответ клиента, новая покупка" />
            <SubItem label="Ежедневная сводка" hint="Скоро — сводка по продажам за день утром 09:00" muted />
            <SubItem label="Email-дублирование" hint="Скоро — те же уведомления копией на email" muted />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------ small subcomponents ------ */
function SectionTitle({ children, style }) {
  return (
    <div style={{ fontFamily: "Manrope", fontSize: "10px", fontWeight: 700, letterSpacing: "0.14em",
                  textTransform: "uppercase", color: "#8a8a89", marginBottom: "10px", ...style }}>
      {children}
    </div>
  );
}

function SubItem({ label, hint, active, muted }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 0",
                  opacity: muted ? 0.6 : 1 }}>
      <span style={{ width: "8px", height: "8px", borderRadius: "50%",
                     background: active ? "#16a34a" : "#d1d5db", flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: "11px", color: "#8a8a89" }}>{hint}</div>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{ background: "#f4f4f3", borderRadius: "8px", height: "120px",
                  animation: "pulse 1.4s infinite" }} />
  );
}

const smallBtn = {
  background: "white", border: "1px solid #d1d5db", borderRadius: "4px",
  padding: "8px 12px", cursor: "pointer", fontFamily: "Manrope",
  fontSize: "11px", fontWeight: 600, letterSpacing: "0.05em",
  textTransform: "uppercase", color: "#374151", display: "flex", alignItems: "center", gap: "4px",
};
